// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.
import { arrIncludes, asString, getLazy, getNavigator, isBoolean, isNullOrUndefined, isNumber, isString, strLower, strStartsWith, throwError } from "@nevware21/ts-utils";
import { _DYN_ATTRIBUTES, _DYN_BASE_DATA, _DYN_CONFIG, _DYN_DATA, _DYN_DURATION, _DYN_I_KEY, _DYN_KIND, _DYN_NAME, _DYN_PARENT_SPAN_CONTEXT, _DYN_SPAN_CONTEXT, _DYN_SPAN_ID, _DYN_START_TIME, _DYN_STATUS, _DYN_TRACE_FLAGS, _DYN_TRACE_ID, _DYN_TRACE_STATE, _DYN_USER_AGENT } from "../__DynamicConstants";
import { STR_NOT_SPECIFIED } from "../constants/InternalConstants";
import { getHttpClientIp, getHttpHost, getHttpMethod, getHttpScheme, getHttpStatusCode, getHttpUrl, getSyntheticType, getUserAgent } from "../internal/commonUtils";
import { hrTimeToMilliseconds } from "../internal/timeHelpers";
import { ATTR_CLIENT_ADDRESS, ATTR_CLIENT_PORT, ATTR_ENDUSER_ID, ATTR_ENDUSER_PSEUDO_ID, ATTR_ERROR_TYPE, ATTR_EXCEPTION_MESSAGE, ATTR_EXCEPTION_STACKTRACE, ATTR_EXCEPTION_TYPE, ATTR_HTTP_REQUEST_METHOD, ATTR_HTTP_RESPONSE_STATUS_CODE, ATTR_NETWORK_LOCAL_ADDRESS, ATTR_NETWORK_LOCAL_PORT, ATTR_NETWORK_PEER_ADDRESS, ATTR_NETWORK_PEER_PORT, ATTR_NETWORK_PROTOCOL_NAME, ATTR_NETWORK_PROTOCOL_VERSION, ATTR_NETWORK_TRANSPORT, ATTR_SERVER_ADDRESS, ATTR_SERVER_PORT, ATTR_URL_FULL, ATTR_URL_PATH, ATTR_URL_QUERY, ATTR_URL_SCHEME, ATTR_USER_AGENT_ORIGINAL, EXP_ATTR_ENDUSER_ID, EXP_ATTR_ENDUSER_PSEUDO_ID, EXP_ATTR_SYNTHETIC_TYPE, SEMATTRS_DB_NAME, SEMATTRS_DB_OPERATION, SEMATTRS_DB_STATEMENT, SEMATTRS_DB_SYSTEM, SEMATTRS_ENDUSER_ID, SEMATTRS_EXCEPTION_MESSAGE, SEMATTRS_EXCEPTION_STACKTRACE, SEMATTRS_EXCEPTION_TYPE, SEMATTRS_HTTP_CLIENT_IP, SEMATTRS_HTTP_FLAVOR, SEMATTRS_HTTP_HOST, SEMATTRS_HTTP_METHOD, SEMATTRS_HTTP_ROUTE, SEMATTRS_HTTP_SCHEME, SEMATTRS_HTTP_STATUS_CODE, SEMATTRS_HTTP_TARGET, SEMATTRS_HTTP_URL, SEMATTRS_HTTP_USER_AGENT, SEMATTRS_NET_HOST_IP, SEMATTRS_NET_HOST_NAME, SEMATTRS_NET_HOST_PORT, SEMATTRS_NET_PEER_IP, SEMATTRS_NET_PEER_NAME, SEMATTRS_NET_PEER_PORT, SEMATTRS_NET_TRANSPORT, SEMATTRS_PEER_SERVICE, SEMATTRS_RPC_GRPC_STATUS_CODE, SEMATTRS_RPC_SYSTEM } from "../otel/attribute/SemanticConventions";
import { createAttributeContainer } from "../otel/attribute/attributeContainer";
import { fieldRedaction } from "../utils/EnvUtils";
import { toISOString } from "../utils/HelperFuncs";
/**
 * Azure SDK namespace.
 * @internal
 */
var AzNamespace = "az.namespace";
var AzResourceNamespace = "azure.resource_provider.namespace";
var PORT_REGEX = (/*#__PURE__*/getLazy(function () { return new RegExp(/(https?)(:\/\/.*)(:\d+)(\S*)/); }));
var HTTP_DOT = "http.";
var _MS_PROCESSED_BY_METRICS_EXTRACTORS = "_MS.ProcessedByMetricExtractors";
/**
 * Legacy HTTP semantic convention values
 * @internal
 */
var _ignoreSemanticValues = (/*#__PURE__*/getLazy(_initIgnoreSemanticValues));
/* #__NO_SIDE_EFFECTS__ */
function _initIgnoreSemanticValues() {
    return [
        // Internal Microsoft attributes
        _MS_PROCESSED_BY_METRICS_EXTRACTORS,
        // Legacy HTTP semantic values
        SEMATTRS_NET_PEER_IP,
        SEMATTRS_NET_PEER_NAME,
        SEMATTRS_NET_HOST_IP,
        SEMATTRS_PEER_SERVICE,
        SEMATTRS_HTTP_USER_AGENT,
        SEMATTRS_HTTP_METHOD,
        SEMATTRS_HTTP_URL,
        SEMATTRS_HTTP_STATUS_CODE,
        SEMATTRS_HTTP_ROUTE,
        SEMATTRS_HTTP_HOST,
        SEMATTRS_DB_SYSTEM,
        SEMATTRS_DB_STATEMENT,
        SEMATTRS_DB_OPERATION,
        SEMATTRS_DB_NAME,
        SEMATTRS_RPC_SYSTEM,
        SEMATTRS_RPC_GRPC_STATUS_CODE,
        SEMATTRS_EXCEPTION_TYPE,
        SEMATTRS_EXCEPTION_MESSAGE,
        SEMATTRS_EXCEPTION_STACKTRACE,
        SEMATTRS_HTTP_SCHEME,
        SEMATTRS_HTTP_TARGET,
        SEMATTRS_HTTP_FLAVOR,
        SEMATTRS_NET_TRANSPORT,
        SEMATTRS_NET_HOST_NAME,
        SEMATTRS_NET_HOST_PORT,
        SEMATTRS_NET_PEER_PORT,
        SEMATTRS_HTTP_CLIENT_IP,
        SEMATTRS_ENDUSER_ID,
        HTTP_DOT + "status_text",
        // http Semabtic conventions
        ATTR_CLIENT_ADDRESS,
        ATTR_CLIENT_PORT,
        ATTR_SERVER_ADDRESS,
        ATTR_SERVER_PORT,
        ATTR_URL_FULL,
        ATTR_URL_PATH,
        ATTR_URL_QUERY,
        ATTR_URL_SCHEME,
        ATTR_ERROR_TYPE,
        ATTR_NETWORK_LOCAL_ADDRESS,
        ATTR_NETWORK_LOCAL_PORT,
        ATTR_NETWORK_PROTOCOL_NAME,
        ATTR_NETWORK_PEER_ADDRESS,
        ATTR_NETWORK_PEER_PORT,
        ATTR_NETWORK_PROTOCOL_VERSION,
        ATTR_NETWORK_TRANSPORT,
        ATTR_USER_AGENT_ORIGINAL,
        ATTR_HTTP_REQUEST_METHOD,
        ATTR_HTTP_RESPONSE_STATUS_CODE,
        ATTR_EXCEPTION_TYPE,
        ATTR_EXCEPTION_MESSAGE,
        ATTR_EXCEPTION_STACKTRACE,
        EXP_ATTR_ENDUSER_ID,
        EXP_ATTR_ENDUSER_PSEUDO_ID,
        EXP_ATTR_SYNTHETIC_TYPE,
        // Azure SDK attributes are not included as they are not part of the span attributes
        AzNamespace,
        AzResourceNamespace,
        "az.client_request_id",
        "azure.client.id",
        "az.service_request_id",
        "azure.service.request.id"
    ];
}
function _applyExtValue(ext, extName, name, value, overwriteExt) {
    if (isString(value) || isNumber(value) || isBoolean(value)) {
        var target = ext[extName] = ext[extName] || {};
        _applyValue(target, name, value, overwriteExt);
    }
}
function _applyValue(target, name, value, overwriteTarget) {
    if (target) {
        if (isString(value) || isNumber(value) || isBoolean(value)) {
            var targetValue = target[name];
            if (!overwriteTarget && (targetValue || isString(targetValue) || isNumber(targetValue) || isBoolean(targetValue))) {
                value = targetValue;
            }
            target[name] = value;
        }
    }
    return target;
}
function _populateExtensionsFromSpan(telemetryItem, span, container) {
    var ext = telemetryItem.ext = telemetryItem.ext || {};
    // Map OpenTelemetry enduser attributes to Application Insights user attributes
    var endUserId = container.get(ATTR_ENDUSER_ID);
    if (endUserId) {
        _applyExtValue(ext, "user", "authId", asString(endUserId), false);
    }
    var endUserPseudoId = container.get(ATTR_ENDUSER_PSEUDO_ID);
    if (endUserPseudoId) {
        _applyExtValue(ext, "user", "id", asString(endUserPseudoId), false);
    }
}
/**
 * Check to see if the key is in the list of known properties to ignore (exclude)
 * from part C properties population.
 * @param key - the property key to check
 * @param contextKeys - The current context keys
 * @returns true if the key should be ignored, false otherwise
 */
function _isIgnorePartCKey(key) {
    var result = false;
    if (arrIncludes(_ignoreSemanticValues.v, key)) {
        // The key is in set of known keys to ignore
        result = true;
    }
    else if (strStartsWith(key, "microsoft.")) {
        // Ignoring all ALL keys starting with "microsoft."
        result = true;
    }
    return result;
}
function _populatePartC(item, container) {
    if (container) {
        var partC_1 = item[_DYN_DATA /* @min:%2edata */] = (item[_DYN_DATA /* @min:%2edata */] || {});
        _applyValue(partC_1, "httpHost", getHttpHost(container), false);
        _applyValue(partC_1, "httpScheme", getHttpScheme(container), false);
        _applyValue(partC_1, "httpClientIp", getHttpClientIp(container), false);
        _applyValue(partC_1, "httpUserAgent", getUserAgent(container), false);
        _applyValue(partC_1, "httpRoute", container.get(SEMATTRS_HTTP_ROUTE), false);
        _applyValue(partC_1, "netPeerName", container.get(SEMATTRS_NET_PEER_NAME), false);
        _applyValue(partC_1, "netPeerPort", container.get(SEMATTRS_NET_PEER_PORT), false);
        _applyValue(partC_1, "netPeerIp", container.get(SEMATTRS_NET_PEER_IP), false);
        _applyValue(partC_1, "peerService", container.get("service.name") || container.get(SEMATTRS_PEER_SERVICE), false);
        var syntheticSource = getSyntheticType(container);
        if (!!syntheticSource) {
            _applyValue(partC_1, "userAgentSyntheticType", syntheticSource, false);
        }
        var nav = getNavigator();
        if (nav && nav[_DYN_USER_AGENT /* @min:%2euserAgent */]) {
            _applyValue(partC_1, "userAgentOriginal", nav[_DYN_USER_AGENT /* @min:%2euserAgent */], false);
        }
        // Azure SDK fields
        _applyValue(partC_1, "azureClientRequestId", container.get("azure.client.id") || container.get("az.client_request_id"), false);
        _applyValue(partC_1, "azureServiceRequestId", container.get("azure.service.request.id") || container.get("az.service_request_id"), false);
        var sampleRate = container.get("microsoft.sample_rate");
        if (!isNullOrUndefined(sampleRate)) {
            _applyValue(partC_1, "sampleRate", sampleRate, false);
        }
        container.forEach(function (key, value) {
            // Avoid duplication ignoring fields already mapped.
            if (!_isIgnorePartCKey(key)) {
                partC_1[key] = value;
            }
        });
    }
}
function _populateHttpProperties(otelSpanTelemetry, container, httpMethod, config) {
    if (httpMethod) {
        // Step the HTTP method
        _applyValue(otelSpanTelemetry, "httpMethod", httpMethod, false);
        // HTTP Dependency
        var httpUrl = getHttpUrl(container);
        if (httpUrl) {
            try {
                // Remove default port
                var res = PORT_REGEX.v.exec(asString(httpUrl));
                if (res !== null) {
                    var protocol = res[1];
                    var port = res[3];
                    if ((protocol === "https" && port === ":443") ||
                        (protocol === "http" && port === ":80")) {
                        // Drop port
                        httpUrl = res[1] + res[2] + res[4];
                    }
                }
            }
            catch (_a) {
                /* no-op */
            }
            _applyValue(otelSpanTelemetry, "httpUrl", fieldRedaction(asString(httpUrl), config), false);
        }
        var httpStatusCode = getHttpStatusCode(container);
        if (httpStatusCode) {
            _applyValue(otelSpanTelemetry, "httpStatusCode", httpStatusCode, false);
        }
    }
}
function _populateDbProperties(otelSpanTelemetry, container, dbSystem) {
    if (dbSystem) {
        _applyValue(otelSpanTelemetry, "dbSystem", dbSystem, false);
        // Set the statement
        _applyValue(otelSpanTelemetry, "dbStatement", container.get(SEMATTRS_DB_STATEMENT) || container.get(SEMATTRS_DB_OPERATION), false);
        _applyValue(otelSpanTelemetry, "dbName", container.get(SEMATTRS_DB_NAME) || dbSystem || "<undefined>", false);
    }
}
function _populateRpcDependencyProperties(otelSpanTelemetry, container, rpcSystem) {
    if (rpcSystem) {
        _applyValue(otelSpanTelemetry, "rpcSystem", strLower(rpcSystem), false);
        _applyValue(otelSpanTelemetry, "rpcGrpcStatusCode", container.get(SEMATTRS_RPC_GRPC_STATUS_CODE), false);
    }
}
function _createOTelSpanTelemetryItem(core, span) {
    var container = span.attribContainer || createAttributeContainer(span[_DYN_ATTRIBUTES /* @min:%2eattributes */]);
    var spanCtx = span[_DYN_SPAN_CONTEXT /* @min:%2espanContext */]();
    var statusCode = span[_DYN_STATUS /* @min:%2estatus */] ? span[_DYN_STATUS /* @min:%2estatus */].code : 0 /* eOTelSpanStatusCode.UNSET */;
    var spanTelemetry = {
        name: span[_DYN_NAME /* @min:%2ename */],
        kind: span[_DYN_KIND /* @min:%2ekind */],
        startTime: toISOString(new Date(hrTimeToMilliseconds(span[_DYN_START_TIME /* @min:%2estartTime */]))),
        success: statusCode !== 0 /* eOTelSpanStatusCode.UNSET */ ? statusCode !== 2 /* eOTelSpanStatusCode.ERROR */ : (Number(getHttpStatusCode(container)) || 0) < 400,
        duration: hrTimeToMilliseconds(span[_DYN_DURATION /* @min:%2eduration */])
        //azureResourceProvider: undefined, // Not currently supported
        //links: UNDEFINED_VALUE,         // Not currently supported
    };
    // Set the parentId if available
    var parentCtx = span[_DYN_PARENT_SPAN_CONTEXT /* @min:%2eparentSpanContext */] || (spanCtx ? spanCtx.parentCtx : null);
    _applyValue(spanTelemetry, "parentId", span.parentSpanId || (parentCtx ? parentCtx[_DYN_SPAN_ID /* @min:%2espanId */] : null), false);
    if (spanCtx) {
        var traceState = spanCtx[_DYN_TRACE_STATE /* @min:%2etraceState */];
        if (traceState && !traceState.isEmpty) {
            _applyValue(spanTelemetry, "traceState", traceState.hdrs(1)[0], false);
        }
    }
    _populateHttpProperties(spanTelemetry, container, getHttpMethod(container), core[_DYN_CONFIG /* @min:%2econfig */]);
    _populateDbProperties(spanTelemetry, container, container.get(SEMATTRS_DB_SYSTEM));
    _populateRpcDependencyProperties(spanTelemetry, container, container.get(SEMATTRS_RPC_SYSTEM));
    _applyValue(spanTelemetry, "azureResourceProvider", container.get(AzResourceNamespace) || container.get(AzNamespace), false);
    return _createTelemetryItem(spanTelemetry, span, "OTelSpan", "Ms.Web.Span");
}
function _createTelemetryItem(item, span, baseType, eventName) {
    eventName = eventName || STR_NOT_SPECIFIED;
    if (isNullOrUndefined(item) ||
        isNullOrUndefined(baseType) ||
        isNullOrUndefined(eventName)) {
        throwError("Input doesn't contain all required fields");
    }
    var iKey = "";
    if (item[_DYN_I_KEY /* @min:%2eiKey */]) {
        iKey = item[_DYN_I_KEY /* @min:%2eiKey */];
        delete item[_DYN_I_KEY /* @min:%2eiKey */];
    }
    var telemetryItem = {
        name: eventName,
        time: toISOString(span.endTime ? new Date(hrTimeToMilliseconds(span.endTime)) : new Date()),
        iKey: iKey,
        ext: {},
        baseType: baseType,
        baseData: item,
        data: {} // part C
    };
    return telemetryItem;
}
/**
 * Create an extended common schema telemetry item from the provided span
 * @param core - The app insights core instance
 * @param span - The span to create the telemetry item from
 * @returns A new extended telemetry item or null if the span kind is not supported
 */
export function createExtendedTelemetryItemFromSpan(core, span) {
    var telemetryItem = _createOTelSpanTelemetryItem(core, span);
    var container = span.attribContainer || createAttributeContainer(span[_DYN_ATTRIBUTES /* @min:%2eattributes */]);
    if (telemetryItem) {
        // Set the Span common schema fields
        // Set start time for the telemetry item from the event, not the time it is being processed (the default)
        // The channel envelope creator uses this value when creating the envelope only when defined, otherwise it
        // uses the time when the item is being processed
        var partB = telemetryItem[_DYN_BASE_DATA /* @min:%2ebaseData */] = telemetryItem[_DYN_BASE_DATA /* @min:%2ebaseData */] || {};
        partB[_DYN_START_TIME /* @min:%2estartTime */] = new Date(hrTimeToMilliseconds(span[_DYN_START_TIME /* @min:%2estartTime */]));
        var spanContext = span[_DYN_SPAN_CONTEXT /* @min:%2espanContext */]();
        if (spanContext) {
            // Add dt extension to the telemetry item
            var ext = telemetryItem.ext = telemetryItem.ext || {};
            var dt = ext["dt"] = ext["dt"] || {};
            // Don't overwrite any existing values
            _applyValue(dt, "spanId", spanContext[_DYN_SPAN_ID /* @min:%2espanId */], false);
            _applyValue(dt, "traceId", spanContext[_DYN_TRACE_ID /* @min:%2etraceId */], false);
            _applyValue(dt, "traceFlags", spanContext[_DYN_TRACE_FLAGS /* @min:%2etraceFlags */], false);
        }
        _populateExtensionsFromSpan(telemetryItem, span, container);
        _populatePartC(telemetryItem, container);
        // Azure SDK
    }
    return telemetryItem;
}
//# sourceMappingURL=extSpanUtils.js.map